import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './SystemHealth.css';

function SystemHealth() {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [creatingBackup, setCreatingBackup] = useState(false);
  const [backupSuccess, setBackupSuccess] = useState('');

  useEffect(() => {
    fetchHealth();
  }, []);

  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      fetchHealth();
    }, 10000); // Refresh every 10 seconds

    return () => clearInterval(interval);
  }, [autoRefresh]);

  const fetchHealth = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await axios.get('/api/admin/health');
      setHealth(response.data);
      setLastUpdate(new Date());
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch health status');
      setHealth(null);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'healthy': return '#10b981';
      case 'warning': return '#f59e0b';
      case 'unhealthy': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'healthy': return '✓';
      case 'warning': return '⚠';
      case 'unhealthy': return '✗';
      default: return '?';
    }
  };

  const formatUptime = (seconds) => {
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);

    if (days > 0) return `${days}d ${hours}h ${minutes}m`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  const formatTimestamp = (date) => {
    return date.toLocaleTimeString();
  };

  const createBackup = async () => {
    setCreatingBackup(true);
    setError('');
    setBackupSuccess('');

    try {
      await axios.post('/api/admin/backups/create');
      setBackupSuccess('Backup created successfully');
      await fetchHealth(); // Refresh health to show new backup
      setTimeout(() => setBackupSuccess(''), 5000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create backup');
    } finally {
      setCreatingBackup(false);
    }
  };

  if (loading && !health) {
    return (
      <div className="health-page">
        <div className="page-header">
          <h1>System Health</h1>
        </div>
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading health status...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="health-page">
      <div className="page-header">
        <div className="header-left">
          <h1>System Health</h1>
          <p className="page-subtitle">Real-time system monitoring and diagnostics</p>
        </div>
        <div className="header-actions">
          <label className="auto-refresh-toggle">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
            />
            <span>Auto-refresh (10s)</span>
          </label>
          <button className="btn btn-primary" onClick={fetchHealth} disabled={loading}>
            {loading ? 'Refreshing...' : 'Refresh Now'}
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger">
          {error}
        </div>
      )}

      {backupSuccess && (
        <div className="alert alert-success">
          {backupSuccess}
        </div>
      )}

      {lastUpdate && (
        <div className="last-update">
          Last updated: {formatTimestamp(lastUpdate)}
        </div>
      )}

      {health && (
        <>
          {/* Overall Status */}
          <div className="status-overview">
            <div className="status-card overall-status" style={{ borderColor: getStatusColor(health.status) }}>
              <div className="status-icon" style={{ background: getStatusColor(health.status) }}>
                {getStatusIcon(health.status)}
              </div>
              <div className="status-info">
                <h2>Overall Status</h2>
                <p className="status-label" style={{ color: getStatusColor(health.status) }}>
                  {health.status.toUpperCase()}
                </p>
              </div>
            </div>

            <div className="status-card">
              <div className="status-info">
                <h3>Uptime</h3>
                <p className="status-value">{formatUptime(health.uptime)}</p>
              </div>
            </div>

            <div className="status-card">
              <div className="status-info">
                <h3>Response Time</h3>
                <p className="status-value">{health.responseTime}ms</p>
              </div>
            </div>

            <div className="status-card">
              <div className="status-info">
                <h3>Timestamp</h3>
                <p className="status-value">{new Date(health.timestamp).toLocaleString()}</p>
              </div>
            </div>
          </div>

          {/* Health Checks */}
          <div className="health-checks">
            <h2>Health Checks</h2>

            {/* Database Check */}
            {health.checks.database && (
              <div className="check-card">
                <div className="check-header">
                  <div className="check-title">
                    <span
                      className="check-status-icon"
                      style={{ background: getStatusColor(health.checks.database.status) }}
                    >
                      {getStatusIcon(health.checks.database.status)}
                    </span>
                    <h3>Database Connection</h3>
                  </div>
                  <span
                    className="check-badge"
                    style={{
                      background: getStatusColor(health.checks.database.status) + '20',
                      color: getStatusColor(health.checks.database.status)
                    }}
                  >
                    {health.checks.database.status}
                  </span>
                </div>
                <div className="check-body">
                  <p>{health.checks.database.message}</p>
                  {health.checks.database.responseTime && (
                    <div className="check-metric">
                      <span className="metric-label">Response Time:</span>
                      <span className="metric-value">{health.checks.database.responseTime}ms</span>
                    </div>
                  )}
                  {health.checks.database.error && (
                    <div className="check-error">Error: {health.checks.database.error}</div>
                  )}
                </div>
              </div>
            )}

            {/* Database Pool */}
            {health.checks.databasePool && (
              <div className="check-card">
                <div className="check-header">
                  <div className="check-title">
                    <span
                      className="check-status-icon"
                      style={{ background: getStatusColor(health.checks.databasePool.status) }}
                    >
                      {getStatusIcon(health.checks.databasePool.status)}
                    </span>
                    <h3>Database Connection Pool</h3>
                  </div>
                  <span
                    className="check-badge"
                    style={{
                      background: getStatusColor(health.checks.databasePool.status) + '20',
                      color: getStatusColor(health.checks.databasePool.status)
                    }}
                  >
                    {health.checks.databasePool.status}
                  </span>
                </div>
                <div className="check-body">
                  <div className="metrics-grid">
                    <div className="check-metric">
                      <span className="metric-label">Total Connections:</span>
                      <span className="metric-value">{health.checks.databasePool.totalCount}</span>
                    </div>
                    <div className="check-metric">
                      <span className="metric-label">Idle Connections:</span>
                      <span className="metric-value">{health.checks.databasePool.idleCount}</span>
                    </div>
                    <div className="check-metric">
                      <span className="metric-label">Waiting Requests:</span>
                      <span className="metric-value">{health.checks.databasePool.waitingCount}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Memory Usage */}
            {health.checks.memory && (
              <div className="check-card">
                <div className="check-header">
                  <div className="check-title">
                    <span
                      className="check-status-icon"
                      style={{ background: getStatusColor(health.checks.memory.status) }}
                    >
                      {getStatusIcon(health.checks.memory.status)}
                    </span>
                    <h3>Memory Usage</h3>
                  </div>
                  <span
                    className="check-badge"
                    style={{
                      background: getStatusColor(health.checks.memory.status) + '20',
                      color: getStatusColor(health.checks.memory.status)
                    }}
                  >
                    {health.checks.memory.status}
                  </span>
                </div>
                <div className="check-body">
                  <div className="metrics-grid">
                    <div className="check-metric">
                      <span className="metric-label">RSS:</span>
                      <span className="metric-value">{health.checks.memory.rss}</span>
                    </div>
                    <div className="check-metric">
                      <span className="metric-label">Heap Used:</span>
                      <span className="metric-value">{health.checks.memory.heapUsed}</span>
                    </div>
                    <div className="check-metric">
                      <span className="metric-label">Heap Total:</span>
                      <span className="metric-value">{health.checks.memory.heapTotal}</span>
                    </div>
                    <div className="check-metric">
                      <span className="metric-label">External:</span>
                      <span className="metric-value">{health.checks.memory.external}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Recent Errors */}
            {health.checks.recentErrors && (
              <div className="check-card">
                <div className="check-header">
                  <div className="check-title">
                    <span
                      className="check-status-icon"
                      style={{ background: getStatusColor(health.checks.recentErrors.status) }}
                    >
                      {getStatusIcon(health.checks.recentErrors.status)}
                    </span>
                    <h3>Recent Errors</h3>
                  </div>
                  <span
                    className="check-badge"
                    style={{
                      background: getStatusColor(health.checks.recentErrors.status) + '20',
                      color: getStatusColor(health.checks.recentErrors.status)
                    }}
                  >
                    {health.checks.recentErrors.status}
                  </span>
                </div>
                <div className="check-body">
                  <p>{health.checks.recentErrors.message}</p>
                  <div className="check-metric">
                    <span className="metric-label">Error Count (Last Hour):</span>
                    <span className="metric-value">{health.checks.recentErrors.count}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Monitoring Stats */}
            {health.checks.monitoring && (
              <div className="check-card">
                <div className="check-header">
                  <div className="check-title">
                    <span
                      className="check-status-icon"
                      style={{ background: getStatusColor(health.checks.monitoring.status) }}
                    >
                      {getStatusIcon(health.checks.monitoring.status)}
                    </span>
                    <h3>Monitoring Configs</h3>
                  </div>
                  <span
                    className="check-badge"
                    style={{
                      background: getStatusColor(health.checks.monitoring.status) + '20',
                      color: getStatusColor(health.checks.monitoring.status)
                    }}
                  >
                    {health.checks.monitoring.status}
                  </span>
                </div>
                <div className="check-body">
                  <div className="metrics-grid">
                    <div className="check-metric">
                      <span className="metric-label">Total Configs:</span>
                      <span className="metric-value">{health.checks.monitoring.totalConfigs}</span>
                    </div>
                    <div className="check-metric">
                      <span className="metric-label">Enabled:</span>
                      <span className="metric-value">{health.checks.monitoring.enabledConfigs}</span>
                    </div>
                    <div className="check-metric">
                      <span className="metric-label">Healthy:</span>
                      <span className="metric-value" style={{ color: '#10b981' }}>
                        {health.checks.monitoring.healthyConfigs}
                      </span>
                    </div>
                    <div className="check-metric">
                      <span className="metric-label">Errors:</span>
                      <span className="metric-value" style={{ color: '#ef4444' }}>
                        {health.checks.monitoring.errorConfigs}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Backup Status */}
            {health.checks.backups && (
              <div className="check-card">
                <div className="check-header">
                  <div className="check-title">
                    <span
                      className="check-status-icon"
                      style={{ background: getStatusColor(health.checks.backups.status) }}
                    >
                      {getStatusIcon(health.checks.backups.status)}
                    </span>
                    <h3>Backup System</h3>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <button
                      className="btn btn-sm btn-primary"
                      onClick={createBackup}
                      disabled={creatingBackup}
                      style={{
                        padding: '6px 12px',
                        fontSize: '13px',
                        background: creatingBackup ? '#60a5fa' : '#3b82f6',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: creatingBackup ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        transition: 'background 0.2s'
                      }}
                    >
                      {creatingBackup && (
                        <span style={{
                          display: 'inline-block',
                          width: '12px',
                          height: '12px',
                          border: '2px solid #ffffff',
                          borderTopColor: 'transparent',
                          borderRadius: '50%',
                          animation: 'spin 0.8s linear infinite'
                        }}></span>
                      )}
                      {creatingBackup ? 'Creating Backup...' : 'Run Backup Now'}
                    </button>
                    <span
                      className="check-badge"
                      style={{
                        background: getStatusColor(health.checks.backups.status) + '20',
                        color: getStatusColor(health.checks.backups.status)
                      }}
                    >
                      {health.checks.backups.status}
                    </span>
                  </div>
                </div>
                <div className="check-body">
                  {health.checks.backups.error ? (
                    <div className="check-error">Error: {health.checks.backups.error}</div>
                  ) : (
                    <>
                      <div className="metrics-grid">
                        <div className="check-metric">
                          <span className="metric-label">Scheduled Backups:</span>
                          <span className="metric-value" style={{
                            color: health.checks.backups.scheduledBackupsEnabled ? '#10b981' : '#6b7280'
                          }}>
                            {health.checks.backups.scheduledBackupsEnabled ? 'Enabled' : 'Disabled'}
                          </span>
                        </div>
                        <div className="check-metric">
                          <span className="metric-label">Scheduler Status:</span>
                          <span className="metric-value" style={{
                            color: health.checks.backups.schedulerRunning ? '#10b981' : '#6b7280'
                          }}>
                            {health.checks.backups.schedulerRunning ? 'Running' : 'Stopped'}
                          </span>
                        </div>
                        <div className="check-metric">
                          <span className="metric-label">Frequency:</span>
                          <span className="metric-value">{health.checks.backups.frequency}</span>
                        </div>
                        <div className="check-metric">
                          <span className="metric-label">Next Backup:</span>
                          <span className="metric-value">{health.checks.backups.nextBackupTime}</span>
                        </div>
                        <div className="check-metric">
                          <span className="metric-label">Total Backups:</span>
                          <span className="metric-value">{health.checks.backups.totalBackups}</span>
                        </div>
                        <div className="check-metric">
                          <span className="metric-label">Retention Period:</span>
                          <span className="metric-value">{health.checks.backups.retentionDays} days</span>
                        </div>
                      </div>
                      {health.checks.backups.lastBackupDate && (
                        <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #e5e7eb' }}>
                          <div className="check-metric">
                            <span className="metric-label">Last Backup:</span>
                            <span className="metric-value">
                              {new Date(health.checks.backups.lastBackupDate).toLocaleString()}
                              {' '}({health.checks.backups.lastBackupType})
                            </span>
                          </div>
                          <div className="check-metric">
                            <span className="metric-label">Last Backup Size:</span>
                            <span className="metric-value">
                              {(health.checks.backups.lastBackupSize / 1024 / 1024).toFixed(2)} MB
                            </span>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default SystemHealth;
